import React, { Fragment } from 'react';
import { SectionHeader } from '@components';
import { COLORS, LEFT_INDENTS, orderMap, TOP_INDENTS } from '@constants';
import { useStore } from '@hooks';
import { Button, Icon, RangePicker } from '@ui';
import { camelToSnakeCase, dayjs } from '@utils';
import { notification } from 'antd';
import fileDownload from 'js-file-download';
import { observer } from 'mobx-react';
import { useExportViaEmailModal } from '../../hooks/useExportViaEmailModal';
import OrderDetailsFooter from './components/OrderDetailsFooter';
import { getColumns, getColumnsInner } from './constants';
import { StyledInnerTable, StyledTable } from './styled';

const OrderList = observer(() => {
    const store = useStore();
    const { orderList, downloadOrders, meta, getOrders, updateFilters, updateSorters, filters, pending } =
        store.ordersStore;
    const { isShopify, isManual, isBigCommerce, currentAccount } = store.accountsStore;
    const { openModal } = useExportViaEmailModal();

    const pagination = {
        total: meta.total,
        totalPages: meta.totalPages,
        pageSize: meta.perPage,
        current: meta.page,
        showSizeChanger: false
    };

    const onTableChange = ({ current }, filters, sorter) => {
        updateSorters({
            sort: sorter.order ? camelToSnakeCase(sorter.field) : undefined,
            order: sorter.order ? orderMap[sorter.order] : undefined
        });

        updateFilters({
            klickly_status: filters.klicklyStatus || undefined,
            financial_status: filters.financialStatus || undefined,
            fulfillment_status: filters.fulfillmentStatus || undefined,
            ecommerce_status: filters.ecommerceStatus || undefined
        });

        getOrders({ params: { page: current } });
    };

    const onDatePickerChange = ({ min, max }) => {
        updateFilters({
            created_at_min: min,
            created_at_max: max
        });
        getOrders();
    };

    const dateFormat = 'YYYY/MM/DD';
    const datePickerValue =
        filters.created_at_min && filters.created_at_max
            ? [dayjs(filters.created_at_min, dateFormat), dayjs(filters.created_at_max, dateFormat)]
            : undefined;

    const handleEmailSubmit = async (email) => {
        try {
            const modalResponse = await downloadOrders({ email });

            if (modalResponse.type === 'message') {
                notification.success({
                    message: <div data-e2e='orders_download-processing_message'>Export Queued</div>,
                    description: <div data-e2e='orders_download-processing_description'>{modalResponse.message}</div>,
                    duration: 6
                });
            }
        } catch (error) {
            notification.error({
                message: 'Export Failed',
                description: error?.message || 'An error occurred while processing your export request.',
                duration: 6
            });
            throw error;
        }
    };

    const onDownload = async () => {
        try {
            const response = await downloadOrders();

            if (!response || !response.type) {
                notification.error({
                    message: 'Download Failed',
                    description: 'Unexpected response format. Please try again.',
                    duration: 6
                });
                return;
            }

            if (response.type === 'file') {
                // Immediate CSV download
                fileDownload(response.file, response.fileName);
            } else if (response.type === 'modal') {
                // Show email modal for large exports
                openModal({
                    userEmail: response.userEmail,
                    onSubmit: handleEmailSubmit
                });
            } else if (response.type === 'message') {
                // Async processing - show notification
                notification.success({
                    message: <div data-e2e='orders_download-processing_message'>Download Processing</div>,
                    description: <div data-e2e='orders_download-processing_description'>{response.message}</div>,
                    duration: 6
                });
            } else {
                notification.warning({
                    message: 'Unexpected Response',
                    description: 'The download request returned an unexpected format.',
                    duration: 6
                });
            }
        } catch (error) {
            notification.error({
                message: 'Download Failed',
                description: error?.message || 'An error occurred while processing your download request.',
                duration: 6
            });
        }
    };

    return (
        <Fragment>
            <SectionHeader data-e2e='orders_page_title' description='Orders' type='em0'>
                <div data-e2e='orders_datepicker-search_input'>
                    <RangePicker onChange={onDatePickerChange} value={datePickerValue} />
                </div>
                <Button
                    style={LEFT_INDENTS.M}
                    icon={<Icon size='24px' color={COLORS.$gray60Black} component={() => <Icon.DownloadIcon />} />}
                    onClick={onDownload}
                    data-e2e='orders_download-csv_btn'
                >
                    Download CSV
                </Button>
            </SectionHeader>
            <StyledTable
                columns={getColumns({ isShopify, isManual, isBigCommerce, currentAccount })}
                dataSource={orderList}
                loading={pending}
                rowKey={(record) => record.klicklyOrderNumber}
                onChange={onTableChange}
                pagination={pagination}
                expandPosition='left'
                style={TOP_INDENTS.XL_PLUS}
                expandable={{
                    expandedRowRender: (record) => {
                        const orderItems = record?.orderItems.map((item, key) => ({ ...item, key }));
                        return (
                            <StyledInnerTable
                                data-e2e={`orders_${record.klicklyOrderNumber}-order-details_content`}
                                dataSource={orderItems || []}
                                columns={getColumnsInner(isManual)}
                                pagination={false}
                                borderColor='transparent'
                                footer={() => <OrderDetailsFooter record={record} />}
                            />
                        );
                    }
                }}
                scroll={{
                    x: 1250
                }}
                data-e2e='orders_orders-list_table'
            />
        </Fragment>
    );
});

export default OrderList;
